import React from "react";
import OutlinedInput from "@mui/material/OutlinedInput";
import SvgIcon, { SvgIconProps } from "@mui/material/SvgIcon";
import { createTheme } from "@mui/material/styles";
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { ReactComponent as ArrowUpDownIcon } from "../assets/icons/arrow_up-down.svg";

const CalendarPrevMonthIcon = (props: SvgIconProps) => {
  const { sx, ...rest } = props;
  return React.createElement(SvgIcon, {
    component: ArrowUpDownIcon as any,
    inheritViewBox: true,
    ...rest,
    sx: [{ width: "11px", height: "11px" }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])],
  });
};

const CalendarNextMonthIcon = (props: SvgIconProps) => {
  const { sx, ...rest } = props;
  return React.createElement(SvgIcon, {
    component: ArrowUpDownIcon as any,
    inheritViewBox: true,
    ...rest,
    sx: [
      { width: "11px", height: "11px", transform: "rotate(180deg)" },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ],
  });
};

const theme = createTheme({
  typography: {
    fontFamily: '"EnergyRegular", sans-serif',
    fontWeightRegular: 400,
    fontWeightBold: 700,
  },
  palette: {
    background: {
      default: "#F1F2F5",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "input:-webkit-autofill": {
          WebkitBoxShadow: "0 0 0px 1000px #FFF inset",
          WebkitTextFillColor: "#000",
          transition: "background-color 5000s ease-in-out 0s",
        },
        "input:-webkit-autofill:focus": {
          WebkitBoxShadow: "0 0 0px 1000px #FFF inset",
          WebkitTextFillColor: "#000",
        },
        html: {
          height: "100%",
          margin: 0,
          padding: 0,
          fontFamily: '"EnergyRegular", sans-serif',
        },
        body: {
          height: "100%",
          margin: 0,
          padding: 0,
          width: "100%",
          fontFamily: '"EnergyRegular", sans-serif',
          backgroundColor: "#F1F2F5",
        },
        "#root": {
          height: "100%",
          fontFamily: '"EnergyRegular", sans-serif',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: '"EnergyRegular", sans-serif',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          minWidth: "auto",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          alignItems: "center",
          gap: "4px",
          margin: 0,
        },
        label: {
          lineHeight: 1,
          "&.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.38)",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          fontSize: "calc(12 / var(--base-font) * 1rem)",
          fontFamily: '"EnergyRegular"',
          lineHeight: "18px",
          color: "#1D2433",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          minWidth: "auto",
          margin: 0,
          width: "auto !important",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#767676",
          },
          "& .MuiInputBase-input": {
            // padding: '0px !important',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: "40px",
          fontFamily: '"EnergyRegular"',
          fontSize: "calc(12 / var(--base-font) * 1rem)",
          borderRadius: "28px",
          border: "none",
          lineHeight: "calc(16 / var(--base-font) * 1rem)",
          color: "#434343",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "4px 10px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#767676",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E20074",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E20074",
          },
        },
        input: {
          padding: "8px 12px",
          height: "100%",
          boxSizing: "border-box",
          fontSize: "calc(14 / var(--base-font) * 1rem)",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent !important",
          "&:hover": {
            backgroundColor: "transparent",
          },
          "&.Mui-focusVisible": {
            backgroundColor: "transparent",
          },
          "&:active": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        text: {
          "--variant-textColor": "#E70070",
          "--variant-outlinedColor": "#E70070",
          "&:hover": {
            "--variant-textBg": "#FFEBF5",
            "--variant-textColor": "#000",
            "--variant-outlinedColor": "#000",
            color: "#000",
            boxShadow: "none",
          },
        },
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        item: {
          "&&.Mui-selected": {
            "--variant-textColor": "#E70070",
            "--variant-outlinedColor": "#E70070",
            color: "#E70070",
            "&:hover": {
              backgroundColor: "#FFEBF5",
              "--variant-textBg": "#FFEBF5",
              "--variant-textColor": "#000",
              "--variant-outlinedColor": "#000",
              color: "#000",
            },
          },
          "&&:hover": {
            backgroundColor: "#FFEBF5",
            "--variant-textBg": "#FFEBF5",
            "--variant-textColor": "#000",
            "--variant-outlinedColor": "#000",
            color: "#000",
          },
        },
      },
    },

    MuiSelect: {
      defaultProps: {
        displayEmpty: true,
        input: React.createElement(OutlinedInput, { notched: false }),
      },
      styleOverrides: {
        root: {
          color: "#1D2433",
          border: "none",
          fontSize: "12px",
          "& .MuiSelect-select": {
            padding: "8px 12px",
            minHeight: "40px",
            display: "flex",
            alignItems: "center",
            fontSize: "12px",
          },
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root": {
            padding: "0 9px 0 9px",
          },
        },
        tag: {
          "&.MuiChip-sizeSmall": {
            fontSize: "0.55rem",
            margin: "0px",
          },
        },
        listbox: {
          "&& .MuiAutocomplete-option:hover": {
            backgroundColor: "#FFEBF5",
            color: "#000",
          },
          "&& .MuiAutocomplete-option.Mui-focused": {
            backgroundColor: "#FFEBF5",
            color: "#000",
          },
          "&& .MuiAutocomplete-option[aria-selected='true']": {
            color: "#fff",
            backgroundColor: "#E70070",
          },
          "&& .MuiAutocomplete-option[aria-selected='true']:hover": {
            backgroundColor: "#9F004F",
          },
          "&& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
            color: "#fff",
            backgroundColor: "#E70070",
          },
        },
        option: {
          "&&:hover": {
            backgroundColor: "#FFEBF5",
            color: "#000",
          },
          "&&.Mui-focused": {
            backgroundColor: "#FFEBF5",
            color: "#000",
          },
          "&&[aria-selected='true']": {
            color: "#fff",
            backgroundColor: "#E70070",
            "&:hover": {
              backgroundColor: "#9F004F",
            },
          },
          "&&[aria-selected='true'].Mui-focused": {
            color: "#fff",
            backgroundColor: "#E70070",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          padding: "8px 12px",
          fontSize: "12px",
          color: "#1D2433",
          "&::placeholder": {
            color: "#434343",
            fontSize: "12px",
            opacity: 1,
          },
        },
        root: {
          backgroundColor: "#fff",
          borderRadius: "28px",
        },
      },
    },
    MuiClockNumber: {
      styleOverrides: {
        root: {
          "&&.Mui-selected": {
            backgroundColor: "#E70070",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#E70070",
              opacity: 0.7,
            },
          },
          "&&.Mui-disabled": {
            color: "#ccc",
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          marginLeft: "0px",
          marginRight: "0px",
          color: "#151515",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "12px",
          color: "#000000",
          whiteSpace: "normal",
          wordBreak: "break-word",
          "&&.Mui-selected": {
            backgroundColor: "#FFF",
            color: "#E70070",
            "&:hover": {
              backgroundColor: "#FFEBF5",
              color: "#000",
            },
            "&:hover .MuiCheckbox-root": {
              color: "#9F004F",
            },
            "&:has(.MuiCheckbox-root)": {
              color: "#000",
            },
          },

          "&:hover": {
            backgroundColor: "#FFEBF5",
          },
          "&:hover .MuiCheckbox-root": {
            color: "#9F004F",
          },
          "&:active": {
            backgroundColor: "#FFEBF5",
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          fontSize: "14px",
          width: "18px",
          height: "18px",
          margin: "0",
          padding:"10px",
          lineHeight: "1",
          "&&:hover": {
            backgroundColor: "#FFEBF5",
            color: "#000",
          },
          "&&:focus": {
            color: "#fff",
            backgroundColor: "#E70070",
            border: "unset",
          },
          "&&.Mui-selected": {
            color: "#fff",
            backgroundColor: "#E70070",
            "&:hover": {
              backgroundColor: "#9F004F",
            },
          },
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        header: {
          justifyContent:"space-between",
          marginBottom: "4px",
        },
        weekContainer: {
          gap: "10px",
          justifyContent: "space-between",
          margin: "8px 0",
        },
        weekDayLabel: {
          fontSize: "14px",
          width: "18px",
          height: "18px",
          margin: "0 2px",
        },
        slideTransition: {
          height: "146px",
          minHeight: "146px",
          width: "220px",
          overflow: "hidden",
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          width: "276px",
          height: "240px",
          padding: "12px 28px",
          borderRadius: "24px",
          backgroundColor: "#fff",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.13)",
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
          paddingRight: 0,
          marginTop: 0,
          marginBottom: "8px",
          paddingBottom: "12px",
          borderBottom: "1px solid #8F8F8F",
        },
        switchViewButton: {
          display:"none"
        },
        label: {
          fontSize: "14px",
          marginRight:"0",
        },
      },
    },
    MuiPickersArrowSwitcher: {
      defaultProps: {
        slots: {
          leftArrowIcon: CalendarPrevMonthIcon,
          rightArrowIcon: CalendarNextMonthIcon,
        },
      },
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "row",
          gap: "4px",
          alignItems: "center",
        },
        button: {
          color: "#000",
          padding: "4px",
          borderRadius: "8px",
          "&:hover": {
            backgroundColor: "unset",
          },
        },
      },
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          "& .MuiDialogActions-root": {
            "--variant-textColor": "#E70070",
            "--variant-outlinedColor": "#E70070",
            color: "#E70070",
            "& .MuiButtonBase-root:hover": {
              "--variant-containedBg": "#FFEBF5",
              "--variant-textColor": "#000",
              "--variant-outlinedColor": "#000",
              color: "#000",
            },
          },
        },
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          width: "100%",
          height: "100%",
          maxHeight: "180px",
          display: "grid",
          gridTemplateColumns: "repeat(4, 50px)",
          rowGap: "5px",
          columnGap: "5px",
          justifyItems: "center",
          overflowX: "hidden",
          button: {
            border: "none",
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            padding: 0,
            margin: 0,
            fontSize: "12px",
            fontFamily: '"EnergyRegular", sans-serif',
            color: "#000",
            "&:hover": {
              backgroundColor: "#FFEBF5",
            },
            "&:focus": {
              color: "#fff",
              backgroundColor: "#E70070",
              "&:hover": {
                backgroundColor: "#9F004F !important",
              },
            },
            "&.Mui-selected": {
              color: "#fff",
              backgroundColor: "#E70070 !important",
              "&:hover": {
                backgroundColor: "#9F004F !important",
              },
            },
          },
        },
      },
    },
    MuiMonthCalendar: {
      styleOverrides: {
        root: {
          width: "auto",
          height: "auto",
          display: "grid",
          gridTemplateColumns: "repeat(4, 50px)",
          rowGap: "5px",
          columnGap: "5px",
          justifyItems: "center",
          button: {
            border: "none",
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            padding: 0,
            margin: 0,
            fontSize: "14px",
            fontFamily: '"EnergyRegular", sans-serif',
            color: "#000",
            "&:hover": {
              backgroundColor: "#FFEBF5",
            },
            "&:focus": {
              color: "#fff",
              backgroundColor: "#E70070",
              "&:hover": {
                backgroundColor: "#9F004F !important",
              },
            },
            "&.Mui-selected": {
              color: "#fff",
              backgroundColor: "#E70070 !important",
              "&:hover": {
                backgroundColor: "#9F004F !important",
              },
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: "12px",
          ".disabled &": {
            borderColor: "#434343",
          },
        },
        label: {
          fontSize: "12px",
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '12px',
          fontFamily: '"EnergyRegular"',
          whiteSpace: "normal",
          wordBreak: "break-word",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          "&.MuiPickerPopper-paper": {
            borderRadius: "24px",
            boxShadow:"0px 4px 6px 0px rgba(0,0,0,0.13)"
          },
          "&.MuiMenu-paper, &.MuiPopover-paper": {
            backgroundColor: "#FFF",
            borderRadius: "18px",
            boxShadow: "0px 4px 6px 0px rgba(0,0,0,0.13)",
            padding: "8px 0",
            minWidth: "180px",
          },
          "&.MuiTableContainer-root": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "#FFEBF5",
          },
          "&.Mui-selected": {
            color: "#434343",
            backgroundColor: "#FFEBF5",
          },
          "&.Mui-selected .MuiTypography-root": {
            color: "#434343",
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: "6px",
          borderRadius: "8px",
          backgroundColor: "unset",
          color: "#676767",
          "&.Mui-checked": {
            color: "#E70070",
            backgroundColor: "unset",
          },
          "&.Mui-disabled": {
            color: "#676767",
            backgroundColor: "unset",
          },
          "&:hover": {
            color: "#9F004F",
            backgroundColor: "unset",
          },
          "&.Mui-checked:hover": {
            backgroundColor: "unset",
          },
          "&.Mui-disabled:hover": {
            backgroundColor: "unset",
          },
          "&.MuiCheckbox-indeterminate": {
            color: "#E20074",
            backgroundColor: "unset",
          },
          "&.MuiCheckbox-indeterminate.Mui-checked": {
            color: "#E20074",
            backgroundColor: "unset",
          },
          "&.MuiCheckbox-indeterminate:hover": {
            backgroundColor: "unset",
          },
          "&.MuiCheckbox-indeterminate.Mui-disabled": {
            color: "#E20074",
            backgroundColor: "unset",
          },
          "&.MuiCheckbox-indeterminate.Mui-checked:hover": {
            backgroundColor: "unset",
          },
          "&.MuiCheckbox-indeterminate.Mui-checked.Mui-disabled": {
            color: "#E20074",
            backgroundColor: "unset",
          },
        },
      },
    },
  },
});

export default theme;
